import { Immutable, applyPatches } from "immer";
import type { PrimitiveAtom, useStore } from "jotai";

import { reportError } from "@/utils/errors";

import {
    Action,
    LayoutEditState,
    createCopyPayload,
    findBlockIndex,
    zCopyPayload,
} from "./layoutEditState";
import { LayoutEditPatch } from "./undoHandler";

interface KeyboardHandlerOptions {
    store: ReturnType<typeof useStore>;
    stateAtom: PrimitiveAtom<Immutable<LayoutEditState>>;
    dispatch: (action: Action) => void;
    undo: () => Array<LayoutEditPatch>;
}

export function setupKeyboardHandlers({
    store,
    stateAtom,
    dispatch,
    undo,
}: KeyboardHandlerOptions) {
    const keydown = (ev: KeyboardEvent) => {
        if ((ev.ctrlKey || ev.metaKey) && ev.key === "z") {
            // user clicked ctrl+z
            ev.preventDefault();
            const patches = undo();
            for (const patch of patches) {
                if (patch.block === null) {
                    store.set(stateAtom, oldValue => {
                        const newValue = applyPatches(oldValue, patch.inversePatches);
                        return newValue;
                    });
                } else {
                    const state = store.get(stateAtom);

                    const blockAtom = state.layout.blocks[patch.block];

                    if (!blockAtom) {
                        reportError(new Error("Block not found in undo"));
                    }

                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    store.set(blockAtom, (oldValue: any) => {
                        const newValue = applyPatches(oldValue, patch.inversePatches);
                        return newValue;
                    });
                }
            }
        }
    };

    const onCopy = (ev: ClipboardEvent) => {
        const state = store.get(stateAtom);
        if (!state.activeBlock) {
            return;
        }

        // Check if ev.target is a input element and has some selection
        if (
            (ev.target instanceof HTMLInputElement || ev.target instanceof HTMLTextAreaElement) &&
            ev.target.selectionStart !== ev.target.selectionEnd
        ) {
            return;
        }

        const payload = createCopyPayload(store, state.activeBlock, state);

        ev.clipboardData?.setData("text/plain", JSON.stringify(payload));

        ev.preventDefault();
    };

    const onPaste = (ev: ClipboardEvent) => {
        const data = ev.clipboardData?.getData("text/plain");
        if (!data) {
            return;
        }

        let jsonParsedData;
        try {
            jsonParsedData = JSON.parse(data);
        } catch (e) {
            reportError(e, "Failed to parse json", false);
            return;
        }

        const payload = zCopyPayload.safeParse(jsonParsedData);
        if (!payload.success) {
            return;
        }

        const state = store.get(stateAtom);

        const index = (state.activeBlock
            ? findBlockIndex(state, state.activeBlock, store)
            : null) ?? { index: state.layout.order.length - 1, parentId: null };

        dispatch({
            type: "paste",
            payload: payload.data,
            index: index.index + 1,
            parentId: index.parentId,
        });

        ev.preventDefault();
    };

    document.addEventListener("keydown", keydown);
    document.addEventListener("copy", onCopy);
    document.addEventListener("paste", onPaste);

    return () => {
        document.removeEventListener("keydown", keydown);
        document.removeEventListener("copy", onCopy);
        document.removeEventListener("paste", onPaste);
    };
}
